import { useCallback, useEffect, useRef } from 'react'

import authReturnUrlService from '@app/services/authReturnUrlService'
import { useJwtSession } from '@app/hooks/useJwtSession'

const IDLE_SESSION_TIMEOUT_MS = 7200 * 1000

const ACTIVITY_EVENTS: ReadonlyArray<keyof WindowEventMap> = ['pointerdown', 'keydown', 'scroll', 'touchstart']

export const useIdleSessionTimeout = (): void => {
  const { session, clearSession } = useJwtSession()
  const timeoutRef = useRef<number | null>(null)
  const lastActivityAtRef = useRef<number>(Date.now())

  const clearIdleTimeout = useCallback((): void => {
    if (timeoutRef.current === null) return

    window.clearTimeout(timeoutRef.current)
    timeoutRef.current = null
  }, [])

  const handleIdleTimeout = useCallback((): void => {
    if (!session) return

    clearIdleTimeout()
    authReturnUrlService.setReturnUrl(window.location.pathname + window.location.search)
    clearSession()
  }, [clearIdleTimeout, clearSession, session])

  const scheduleIdleTimeout = useCallback((): void => {
    clearIdleTimeout()

    if (!session) return

    const remainingTimeout = IDLE_SESSION_TIMEOUT_MS - (Date.now() - lastActivityAtRef.current)

    if (remainingTimeout <= 0) {
      handleIdleTimeout()

      return
    }

    timeoutRef.current = window.setTimeout(handleIdleTimeout, remainingTimeout)
  }, [clearIdleTimeout, handleIdleTimeout, session])

  const handleActivity = useCallback((): void => {
    if (!session) return

    const isIdleTimeoutExpired = Date.now() - lastActivityAtRef.current >= IDLE_SESSION_TIMEOUT_MS

    if (isIdleTimeoutExpired) {
      handleIdleTimeout()

      return
    }

    lastActivityAtRef.current = Date.now()
    scheduleIdleTimeout()
  }, [handleIdleTimeout, scheduleIdleTimeout, session])

  useEffect(() => {
    if (!session) {
      clearIdleTimeout()

      return
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') return

      handleActivity()
    }

    lastActivityAtRef.current = Date.now()
    scheduleIdleTimeout()

    ACTIVITY_EVENTS.forEach(eventName => window.addEventListener(eventName, handleActivity, { passive: true }))
    window.addEventListener('focus', handleActivity)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearIdleTimeout()
      ACTIVITY_EVENTS.forEach(eventName => window.removeEventListener(eventName, handleActivity))
      window.removeEventListener('focus', handleActivity)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [clearIdleTimeout, handleActivity, scheduleIdleTimeout, session])
}
